import { BasePresenter } from "core/presenter";
import { UserRepository, DevolutionReportRepository } from "core/repository";

import Moment from "moment";
import * as FileSaver from "file-saver";

import { fn } from "util";

class DevolutionReportPresenter extends BasePresenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      loading: true,
      filters: {
        dateFrom: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        dateTo: endDate.isValid() ? endDate : Moment().endOf("day")
      },
      pagination: {
        page: 1,
        perPage: 25,
        total: 0
      },
      summary: {
        total: 0
      },
      results: [],
      order: {
        field: "created_at",
        direction: "DESC"
      },
      userSearchOptions: {
        online: true,
        repository: UserRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: filter => this.getUserFilters(filter)
      },
      user: {
        name: "",
        uuid: null
      },
      mode: "user"
    };
  }

  async onStart() {
    this.load();
  }

  msgError(content) {
    this.sendNotification({
      type: "error",
      title: "Oops",
      content: content
    });
  }

  makePagination(perPage, total) {
    this.viewModel.pagination = {
      ...this.viewModel.pagination,
      total: total,
      perPage: perPage
    };
  }

  async load() {
    this.viewModel.loading = true;
    this.viewModel.results = [];
    let resultsPromise;
    this.viewModel.mode = "user";
    resultsPromise = this.loadUserResults();

    await Promise.all([resultsPromise, this.loadSummary(this.viewModel.mode)]);

    this.viewModel.loading = false;
  }

  cleanResults() {
    this.viewModel.results = [];
  }

  async loadUserResults() {
    this.renewCancelToken();
    this.cleanResults();
    const filters = await this.getReportFilters();
    const repositoryResult = await DevolutionReportRepository.getDevolutionReport(
      filters
    );

    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.devolutions;
      const perPage = repositoryResult.data.pagination.per_page;
      const total = repositoryResult.data.pagination.total_items;
      this.makePagination(perPage, total);
    } else if (!repositoryResult.userCancelled) {
      this.msgError(
        "Não foi possível carregar os vendedores.\nTente novamente mais tarde ou entre em contato com o suporte."
      );
    }
  }

  cleanSummary() {
    this.viewModel.summary.total = 0;
  }

  async loadSummary(user) {
    this.cleanSummary();
    const filters = await this.getReportFilters();
    const repositoryResult = await DevolutionReportRepository.getDevolutionTotal(
      filters
    );
    if (repositoryResult.success) {
      this.viewModel.summary = repositoryResult.data;
    }
  }

  async getReportFilters() {
    const { user, filters, pagination } = this.viewModel;
    const { dateFrom, dateTo } = filters;
    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;
    const order = this.getOrder();
    const reportFilters = {
      company_uuid: company_uuid,
      from: Moment(dateFrom)
        .startOf("day")
        .toISOString(),
      to: Moment(dateTo)
        .endOf("day")
        .toISOString(),
      page: pagination.page,
      user: user.uuid,
      count: 25,
      order: order.direction === "DESC" ? `-${order.field}` : order.field
    };

    return reportFilters;
  }

  getUserFilters(query) {
    const filters = [];

    if (query) {
      filters.push({
        connector: "AND",
        field: "name",
        operation: "LIKE",
        value: query
      });
    }

    return filters;
  }

  async onDownload(format) {
    this.viewModel.loading = true;
    if (format === "csv") {
      await this._downloadCSV();
    } else {
      const content = "Ainda não suportamos esse formato";
      this.msgError(content);
    }
    this.viewModel.loading = false;
  }

  async _downloadCSV() {
    const filters = await this.getReportFilters();
    const results = await DevolutionReportRepository.getDevolutionReportCSV(
      filters
    );
    let content = [];
    content = [
      "Data; Valor; Serie; Tipo; Numero do Documento; Devolucao; Vendedor; Nome do Cliente; CPF/CNPJ"
    ];
    results.data.devolutions.forEach(item => {
      const totalValue = fn.centsToReal(item.total_value);
      const salesmanName = item.salesman ? item.salesman.name : "-";
      const customerName = item.customer
        ? item.customer.name
        : "Cliente nao Identificado";
      const customerIdentification = item.customer
        ? item.customer.identification
        : "-";
      const creditCouponCode = item.credit_coupon
        ? item.credit_coupon.code
        : "-";
      const date = Moment(item.created_at).format("DD/MM/YYYY HH:mm");
      const serie = item.sale.document.serie;
      const type = item.sale.document.type;
      const number = item.sale.document.number;
      content.push(
        `${date};${totalValue};${serie};${type};${number};${creditCouponCode};${salesmanName};${customerName};${customerIdentification}`
      );
    });
    const contentBlob = new Blob([content.join("\n")], {
      type: "application/csv;charset=utf-8"
    });
    FileSaver.saveAs(contentBlob, "relatorio-de-devolucao.csv");
  }

  getOrder() {
    const { order } = this.viewModel;

    if (this.viewModel.mode === "sale" && order.field === "document_number") {
      return { direction: order.direction, field: "document.number" };
    }

    return order;
  }

  onSelectUser(selectedUser) {
    if (selectedUser.name === "") {
      this.viewModel.user = selectedUser;
      this.viewModel.mode = "sale";
      this.load();
    }
    this.viewModel.user = selectedUser;
    this.viewModel.pagination.page = 1;

    if (selectedUser.uuid) {
      this.viewModel.order = { field: "sale.activated_at", direction: "ASC" };
    }

    this.load();
  }

  onFilterChange(filters) {
    if (filters.dateFrom && filters.dateTo) {
      window.location.hash = `?&inicio=${Moment(filters.dateFrom).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filters.dateTo).format("DD-MM-YYYY")}`;
    }
    for (const key in filters) {
      if (filters[key] !== this.viewModel.filters[key]) {
        this.viewModel.filters = {
          ...this.viewModel.filters,
          ...filters
        };
        this.viewModel.pagination.page = 1;

        this.load();
        break;
      }
    }
  }

  onPageChange(page) {
    this.viewModel.pagination.page = page;
    this.load();
  }

  onOrderChange(field, direction) {
    this.viewModel.order = { field, direction };
    this.load();
  }
}

export default DevolutionReportPresenter;



// WEBPACK FOOTER //
// ./src/components/app/management/devolution-report/presenter.js